import {APP_INITIALIZER, InjectionToken, LOCALE_ID} from '@angular/core';
import {defaultInterpolationFormat, I18NEXT_SERVICE, I18NextModule, I18NextTitle, ITranslationService} from 'angular-i18next';
import {UserRoles} from 'app/core/helpers/user.roles';
import {AuthService, CurrentUser} from 'app/core/services/auth.service';
import LngDetector from 'i18next-browser-languagedetector';
import HttpApi from 'i18next-http-backend';
import {environment} from '../../environments/environment';
import {I18NextTitleService} from './services/i18-next-title.service';
import {buildInfo} from '../../environments/buildInfo';
import moment from 'moment';
import {en_US, NZ_DATE_CONFIG, NZ_I18N, ru_RU} from 'ng-zorro-antd/i18n';
import {registerLocaleData} from '@angular/common';
import localeRu from '@angular/common/locales/ru';
import {DemoService} from 'app/core/services/demo.service';

registerLocaleData(localeRu);
export const I18NEXT_OPTIONS = new InjectionToken('i18nextOptions');

/*
 * Platform and Environment providers/directives/pipes
 */
export const i18nextOptions = {
    whitelist: ['en', 'ru'],
    supportedLngs: ['en', 'ru'],
    fallbackLng: {
        ru: ['en'],
    },
    debug: !environment.production,
    returnEmptyString: false,
    interpolation: {
        format: I18NextModule.interpolationFormat(defaultInterpolationFormat),
    },
    // backend plugin options
    backend: {
        loadPath: `assets/i18n/{{lng}}.json?v=${buildInfo.version}`,
    },
    // lang detection plugin options
    detection: {
        order: ['cookie', 'navigator'],
        lookupCookie: 'locale',
        caches: ['cookie'],
    },
};

export function i18NextServiceFactory(i18next: ITranslationService, options: any, auth: AuthService, demo: DemoService) {
    return () =>
        auth.init().then((user: CurrentUser) => {
            if (options.backend) {
                if (!user || (user && user.locale !== 'dev')) {
                    i18next.use(HttpApi);
                }
            }
            if (!demo.isDemoHost && user && user.locale) {
                if (user.roles.includes(UserRoles.LocaleWriter)) {
                    options.whitelist.push('dev');
                }
                // if lang valid
                options.lng = options.whitelist.includes(user.locale) ? user.locale : 'en';
                return i18next.init(options);
            }
            // try to detect lang
            return i18next.use(LngDetector).init(options);
        });
}

export const I18N_PROVIDERS = [
    {
        provide: APP_INITIALIZER,
        useFactory: i18NextServiceFactory,
        deps: [I18NEXT_SERVICE, I18NEXT_OPTIONS, AuthService, DemoService],
        multi: true,
    },
    {
        provide: LOCALE_ID,
        deps: [I18NEXT_SERVICE],
        useFactory: localeIdFactory,
    },
    {
        provide: I18NextTitle,
        useClass: I18NextTitleService,
    },
    {
        provide: NZ_I18N,
        useFactory(locale: string) {
            if (locale === 'ru') {
                return ru_RU;
            }
            return en_US;
        },
        deps: [LOCALE_ID],
    },
    {
        provide: NZ_DATE_CONFIG,
        useFactory() {
            return {
                firstDayOfWeek: Number(moment().startOf('isoWeek').format('d')),
            };
        },
        deps: [LOCALE_ID],
    },
];

export /* istanbul ignore next */ function localeIdFactory(i18next: ITranslationService) {
    const lng = i18next.language || i18next.options.lng;
    moment.locale(lng);
    return lng;
}
